import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel,
} from "@/components/ui/alert-dialog";

export function DeleteAccountSection() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [impact, setImpact] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const hasPassword = !!(user && user.has_password);

  useEffect(() => {
    if (!open) return;
    setConfirm(""); setPassword(""); setCode(""); setCodeSent(false); setImpact(null);
    (async () => {
      try { const { data } = await api.get("/account/delete/impact"); setImpact(data); }
      catch { /* non-blocking */ }
    })();
  }, [open]);

  const sendCode = async () => {
    try {
      await api.post("/account/delete/request-code");
      setCodeSent(true);
      toast.success("Confirmation code sent to your email.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send the code."); }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/account/delete", {
        confirm,
        password: hasPassword ? password : undefined,
        code: hasPassword ? undefined : code,
      });
      setOpen(false);
      // The server has already invalidated the session + cleared the cookie. A
      // hard redirect stops all timers/sockets and re-initialises auth cleanly,
      // landing on the login page with a pending-deletion banner. We do NOT stay
      // in the app and the dialog cannot remain usable.
      const purge = data?.purge_at ? `&purge=${encodeURIComponent(data.purge_at)}` : "";
      window.location.replace(`/login?deleted=1${purge}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "group_ownership_unresolved") {
        setImpact((prev) => ({ ...(prev || {}), owned_groups: d.groups || [] }));
        toast.error(d.message || "Resolve the groups you own first.");
      } else {
        toast.error(typeof d === "string" ? d : "Couldn't delete your account.");
      }
      setBusy(false);
    }
  };

  const canDelete =
    confirm.trim() === "DELETE"
    && (hasPassword ? password.length > 0 : code.trim().length === 6)
    && !busy
    // Groups Phase 10 (D9) — a group must never be left without a valid owner.
    && !(impact?.owned_groups || []).length;
  const orgsWithTeam = (impact?.owned_orgs || []).filter((o) => o.member_count > 0);
  const blockingGroups = impact?.owned_groups || [];
  const GROUP_ACTION = {
    transfer_or_delete: "Transfer ownership to another member, or delete the group",
    delete: "Delete the group (you're its only member)",
    delete_draft: "Delete the draft",
    contact_support: "Suspended by Startup Otter — contact support to resolve",
  };

  return (
    <Card className="border-destructive/40 p-6" data-testid="account-danger-zone">
      <h2 className="text-lg font-[640] text-destructive">Delete account</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Move your account into pending deletion for 30 days. You can restore it any time before the purge date — after that it’s permanently removed.
      </p>
      <Button type="button" variant="destructive" className="mt-3" onClick={() => setOpen(true)} data-testid="account-delete-open">
        Delete my account
      </Button>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="account-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" /> Delete your account?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>This signs you out immediately and:</p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>Hides your personal profile and reserves your @handle and URL.</li>
                  <li>Hides your published businesses, capital profiles, events and resources.</li>
                  <li>Suspends your license key and plan access.</li>
                  <li>Starts a 30-day recovery window — you can restore everything by logging in and confirming an emailed code.</li>
                </ul>
                {blockingGroups.length > 0 && (
                  <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm"
                       data-testid="account-delete-groups-blocker">
                    <p className="font-medium text-destructive">
                      Resolve the groups you own first
                    </p>
                    <p className="mt-1 text-muted-foreground">
                      You own {blockingGroups.length} group{blockingGroups.length > 1 ? "s" : ""}.
                      A group can&apos;t be left without an owner, and archiving isn&apos;t enough.
                    </p>
                    <ul className="mt-2 space-y-1.5">
                      {blockingGroups.map((g) => (
                        <li key={g.group_id} className="flex flex-wrap items-center gap-2"
                            data-testid={`account-delete-group-${g.group_id}`}>
                          <a href={g.manage_url} className="font-medium underline">
                            {g.display_name}
                          </a>
                          <span className="text-xs text-muted-foreground">
                            {GROUP_ACTION[g.resolution] || "Resolve ownership"}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {orgsWithTeam.length > 0 && (                  <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-800" data-testid="account-delete-team-warning">
                    You own {orgsWithTeam.length} profile{orgsWithTeam.length > 1 ? "s" : ""} with other team members
                    ({orgsWithTeam.map((o) => o.name).join(", ")}). They’ll lose access while hidden — consider
                    transferring ownership from each profile’s Team settings first.
                  </div>
                )}
                <p>After 30 days your account is permanently anonymized and your email + handles are freed.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-3">
            {hasPassword ? (
              <div className="space-y-1.5">
                <Label>Enter your password</Label>
                <Input type="password" autoComplete="current-password" value={password}
                       onChange={(e) => setPassword(e.target.value)} data-testid="account-delete-password" />
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label>Email confirmation code</Label>
                <div className="flex gap-2">
                  <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code"
                         maxLength={6} inputMode="numeric" data-testid="account-delete-code" />
                  <Button type="button" variant="secondary" onClick={sendCode} data-testid="account-delete-send-code">
                    {codeSent ? "Resend" : "Send code"}
                  </Button>
                </div>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Type <span className="font-mono font-semibold">DELETE</span> to confirm</Label>
              <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="DELETE" data-testid="account-delete-confirm" />
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel data-testid="account-delete-cancel">Cancel</AlertDialogCancel>
            <Button type="button" variant="destructive" disabled={!canDelete} onClick={doDelete} data-testid="account-delete-submit">
              {busy ? "Scheduling…" : "Delete account"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
